import React, {
    useEffect,
    useState,
} from "react";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

import TableStyle from "./TableStyle";

import {
    Table,
    Input,
    Button,
    Typography,
    Space,
    Empty,
    Tag,
    message,
    Popconfirm,
    Tooltip,
} from "antd";

import {
    SearchOutlined,
    SafetyOutlined,
    EditOutlined,
    DeleteOutlined,
    UserOutlined,
    ReloadOutlined,
    TeamOutlined,
    FileTextOutlined,
} from "@ant-design/icons";

import Secure from "./Secure";
import EditStudentAdmissionPopup from "./EditStudentAdmissionPopup";
import AdmissionStuListByClass from "./AdmissionStuListByClass";
import AdmissionDataFormDownload from "./AdmissionDataFormDownload";

const { Title, Text } = Typography;

const PageLoader = () => {
    return (
        <div className="students-admission-loader">
            <div className="students-admission-loader-ring">
                <span />
                <span />
                <span />
            </div>

            <Text>
                Loading student admissions...
            </Text>
        </div>
    );
};

const StudentsAdmission = () => {
    const { selectedYear } = useAcademicYear();

    const [secureStudent, setSecureStudent] =
        useState(null);

    const [students, setStudents] = useState([]);

    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] =
        useState("");

    const [editStudent, setEditStudent] =
        useState(null);

    const [darkMode, setDarkMode] = useState(
        localStorage.getItem("darkMode") === "true"
    );

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
    });

    const getToken = () => {
        return localStorage.getItem("access");
    };

    /*
    |--------------------------------------------------------------------------
    | DARK MODE OBSERVER
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setDarkMode(
                document.body.classList.contains(
                    "dark-mode"
                )
            );
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["class"],
        });

        return () => observer.disconnect();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | FETCH STUDENTS
    |
    | IMPORTANT:
    | Search is sent to Django using ?search=
    | This means searching is GLOBAL across all
    | paginated admission records.
    |--------------------------------------------------------------------------
    */

    const fetchStudents = async (
        page = 1,
        pageSize = pagination.pageSize,
        requestedSearch = searchTerm
    ) => {
        const token = getToken();

        if (!token) {
            window.location.href = "/login";
            return;
        }

        if (!selectedYear?.id) {
            setStudents([]);

            setPagination({
                current: 1,
                pageSize,
                total: 0,
            });

            return;
        }

        try {
            setLoading(true);

            const cleanSearch = String(
                requestedSearch || ""
            ).trim();

            const params = new URLSearchParams();

            params.set(
                "academic_year",
                selectedYear.id
            );

            params.set("page", page);

            params.set("page_size", pageSize);

            /*
            |--------------------------------------------------------------------------
            | GLOBAL SERVER-SIDE SEARCH
            |--------------------------------------------------------------------------
            */

            if (cleanSearch) {
                params.set(
                    "search",
                    cleanSearch
                );
            }

            const res = await api.get(
                `/api/users/students/?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const results =
                res.data?.results || [];

            setStudents(results);

            setPagination({
                current:
                    res.data?.current_page ||
                    page,

                pageSize:
                    res.data?.page_size ||
                    pageSize,

                total:
                    res.data?.count ??
                    res.data?.total ??
                    0,
            });
        } catch (error) {
            console.error(
                "Student admissions fetch error:",
                error
            );

            if (
                error.response?.status === 401
            ) {
                localStorage.removeItem(
                    "access"
                );

                window.location.href =
                    "/login";

                return;
            }

            message.error(
                error.response?.data?.detail ||
                    "Unable to load student admissions."
            );
        } finally {
            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | INITIAL LOAD / ACADEMIC YEAR CHANGE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (!selectedYear?.id) {
            setStudents([]);

            setPagination({
                current: 1,
                pageSize: 50,
                total: 0,
            });

            return;
        }

        fetchStudents(
            1,
            pagination.pageSize,
            searchTerm
        );
    }, [selectedYear]);

    /*
    |--------------------------------------------------------------------------
    | GLOBAL SEARCH
    |
    | Whenever the user searches, wait briefly and then
    | ask Django to search the entire StudentAdmission
    | queryset.
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (!selectedYear?.id) {
            return;
        }

        const timer = setTimeout(() => {
            fetchStudents(
                1,
                pagination.pageSize,
                searchTerm
            );
        }, 350);

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm]);

    /*
    |--------------------------------------------------------------------------
    | DELETE STUDENT
    |--------------------------------------------------------------------------
    */

    const deleteStudent = async (
        student
    ) => {
        try {
            await api.delete(
                `/api/users/students/${student.id}/`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access"
                        )}`,
                    },
                }
            );

            message.success(
                "Student admission deleted successfully."
            );

            /*
            |--------------------------------------------------------------------------
            | After deletion, reload the current page.
            | If a search is active, preserve that search.
            |--------------------------------------------------------------------------
            */

            fetchStudents(
                pagination.current,
                pagination.pageSize,
                searchTerm
            );
        } catch (error) {
            console.error(
                "Delete student error:",
                error
            );

            if (
                error.response?.status === 401
            ) {
                localStorage.removeItem(
                    "access"
                );

                window.location.href =
                    "/login";

                return;
            }

            message.error(
                error.response?.data?.detail ||
                    "Unable to delete student."
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | TABLE COLUMNS
    |--------------------------------------------------------------------------
    */

    const columns = [
        {
            title: "NO.",
            width: 65,
            fixed: "left",
            align: "center",

            render: (_, __, index) => {
                /*
                |--------------------------------------------------------------------------
                | During global search, results start from 1.
                | During normal pagination, preserve global row numbering.
                |--------------------------------------------------------------------------
                */

                const hasSearch =
                    searchTerm.trim().length > 0;

                if (hasSearch) {
                    return index + 1;
                }

                return (
                    (pagination.current - 1) *
                        pagination.pageSize +
                    index +
                    1
                );
            },
        },

        {
            title: "STUDENT",
            dataIndex: "full_name",
            width: 250,

            render: (text) => (
                <div className="student-name-cell">
                    <div className="student-avatar">
                        <UserOutlined />
                    </div>

                    <div className="student-name-content">
                        <strong>
                            {text ||
                                "Unnamed Student"}
                        </strong>

                        <span>
                            Student Admission
                        </span>
                    </div>
                </div>
            ),
        },

        {
            title: "SESSION",
            dataIndex:
                "division_assigned_name",
            responsive: ["md"],
            width: 170,

            render: (text) => (
                <span className="table-secondary-text">
                    {text || "—"}
                </span>
            ),
        },

        {
            title: "GRADE CLASS",
            dataIndex:
                "grade_class_name",
            width: 180,

            render: (text) => (
                <Tag className="grade-class-tag">
                    <TeamOutlined />

                    {text || "Not Assigned"}
                </Tag>
            ),
        },

        {
            title: "SEMESTER",
            dataIndex:
                "semester_status",
            responsive: ["lg"],
            width: 130,

            render: (text) => (
                <span className="table-secondary-text">
                    {text || "—"}
                </span>
            ),
        },

        {
            title: "STATUS",
            dataIndex: "status",
            width: 110,
            align: "center",

            render: (status) => (
                <Tag
                    className={
                        status === "Enrolled" ||
                        status === "Active"
                            ? "status-active"
                            : "status-inactive"
                    }
                >
                    {status || "Unknown"}
                </Tag>
            ),
        },

        {
            title: "ACTIONS",
            fixed: "right",
            width: 245,

            render: (_, student) => (
                <Space
                    size={6}
                    className="student-action-space"
                >
                    <Tooltip title="Edit admission">
                        <Button
                            className="table-action-edit"
                            icon={
                                <EditOutlined />
                            }
                            onClick={() =>
                                setEditStudent(
                                    student
                                )
                            }
                        >
                            Edit
                        </Button>
                    </Tooltip>

                    <Popconfirm
                        title="Delete Student Admission"
                        description={`Are you sure you want to delete ${student.full_name}?`}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okButtonProps={{
                            danger: true,
                        }}
                        onConfirm={() =>
                            deleteStudent(
                                student
                            )
                        }
                    >
                        <Tooltip title="Delete admission">
                            <Button
                                danger
                                className="table-action-delete"
                                icon={
                                    <DeleteOutlined />
                                }
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </Popconfirm>

                    <Tooltip title="Student authentication">
                        <Button
                            className="secure-btn"
                            type="primary"
                            icon={
                                <SafetyOutlined />
                            }
                            onClick={() => {
                                setSecureStudent(
                                    student
                                );

                                setTimeout(() => {
                                    setSecureStudent(
                                        null
                                    );
                                }, 1000);
                            }}
                        >
                            Auth
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className={`students-admission-page ${
                darkMode
                    ? "students-admission-dark"
                    : ""
            }`}
        >
            <TableStyle />

            <div className="students-admission-shell">
                {/* =========================================================
                    HEADER
                ========================================================= */}

                <section className="students-admission-header">
                    <div className="students-admission-header-glow" />

                    <div className="students-admission-header-content">
                        <div className="students-admission-title-group">
                            <div className="students-admission-icon">
                                <TeamOutlined />
                            </div>

                            <div>
                                <Text className="students-admission-eyebrow">
                                    Admissions Management
                                </Text>

                                <Title
                                    level={2}
                                    className="students-admission-title"
                                >
                                    Student Admissions
                                </Title>

                                <Text className="students-admission-subtitle">
                                    Manage registered
                                    students,
                                    admission records
                                    and academic
                                    placement.
                                </Text>
                            </div>
                        </div>

                        <div className="students-admission-year">
                            <span>
                                Academic Year
                            </span>

                            <strong>
                                {selectedYear?.name ||
                                    "Select Year"}
                            </strong>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    DOWNLOAD / CLASS SECTION
                ========================================================= */}

                <section className="admission-download-section">
                    <AdmissionStuListByClass />
                </section>

                {/* =========================================================
                    TOOLBAR
                ========================================================= */}

                <section className="students-admission-toolbar">
                    <div className="students-admission-toolbar-left">
                        <div className="students-admission-search">
                            <SearchOutlined />

                            <Input
                                bordered={false}
                                allowClear
                                placeholder="Search student, class, email or status..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(
                                        e.target
                                            .value
                                    );
                                }}
                            />
                        </div>

                        {searchTerm && (
                            <Tag className="search-result-tag">
                                Searching:{" "}
                                {searchTerm}
                            </Tag>
                        )}
                    </div>

                    <div className="students-admission-toolbar-actions">
                        <Tooltip title="Refresh admissions">
                            <Button
                                className="refresh-admission-btn"
                                icon={
                                    <ReloadOutlined />
                                }
                                loading={loading}
                                onClick={() =>
                                    fetchStudents(
                                        pagination.current,
                                        pagination.pageSize,
                                        searchTerm
                                    )
                                }
                            />
                        </Tooltip>

                        <AdmissionDataFormDownload />
                    </div>
                </section>

                {/* =========================================================
                    TABLE
                ========================================================= */}

                <section className="students-admission-table-section">
                    <div className="students-admission-table-heading">
                        <div>
                            <Text className="table-heading-eyebrow">
                                Registered Students
                            </Text>

                            <div className="table-heading-title">
                                Admission Records
                            </div>
                        </div>

                        <div className="table-record-count">
                            <FileTextOutlined />

                            <span>
                                {pagination.total ||
                                    0}{" "}
                                Records
                            </span>
                        </div>
                    </div>

                    {loading ? (
                        <PageLoader />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={students}
                            rowKey="id"
                            className="students-admission-table"
                            rowClassName={() =>
                                "student-admission-row"
                            }
                            scroll={{
                                x: 1100,
                            }}
                            pagination={{
                                current:
                                    pagination.current,

                                pageSize:
                                    pagination.pageSize,

                                total:
                                    pagination.total,

                                showSizeChanger:
                                    true,

                                pageSizeOptions: [
                                    "10",
                                    "20",
                                    "50",
                                    "100",
                                ],

                                showTotal: (
                                    total
                                ) =>
                                    `Total ${total} students`,

                                onChange: (
                                    page,
                                    pageSize
                                ) => {
                                    fetchStudents(
                                        page,
                                        pageSize,
                                        searchTerm
                                    );
                                },
                            }}
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description={
                                            searchTerm
                                                ? "No students match your search."
                                                : "No student admissions found."
                                        }
                                    />
                                ),
                            }}
                        />
                    )}
                </section>
            </div>

            {/* =============================================================
                SECURE STUDENT
            ============================================================= */}

            {secureStudent && (
                <Secure
                    student={secureStudent}
                />
            )}

            {/* =============================================================
                EDIT STUDENT
            ============================================================= */}

            <EditStudentAdmissionPopup
                visible={!!editStudent}
                student={editStudent}
                onClose={() =>
                    setEditStudent(null)
                }
                refresh={() =>
                    fetchStudents(
                        pagination.current,
                        pagination.pageSize,
                        searchTerm
                    )
                }
            />

            {/* =============================================================
                STYLES
            ============================================================= */}

            <style>
                {`
                    .students-admission-page {
                        --admission-navy: #062A4B;
                        --admission-blue: #063A6B;
                        --admission-orange: #C0750D;
                        --admission-gold: #F6C944;
                        --admission-yellow: #EFA81C;
                        --admission-page: #F4F7F9;
                        --admission-white: #FFFFFF;
                        --admission-text: #142536;
                        --admission-muted: #6B7280;
                        --admission-border: #E0E7EC;
                        --admission-green: #4FAE22;

                        width: 100%;
                        min-height: 100vh;
                        padding: 18px;

                        background:
                            linear-gradient(
                                180deg,
                                #f7f9fb 0%,
                                #f2f5f8 100%
                            );

                        box-sizing: border-box;
                    }

                    .students-admission-dark {
                        --admission-page: #0B1117;
                        --admission-white: #141C24;
                        --admission-text: #F0F4F8;
                        --admission-muted: #A6B0BA;
                        --admission-border: #273541;

                        background:
                            linear-gradient(
                                180deg,
                                #0b1117 0%,
                                #0e151c 100%
                            );
                    }

                    .students-admission-shell {
                        width: 100%;
                        max-width: 1800px;
                        margin: 0 auto;
                    }

                    .students-admission-header {
                        position: relative;
                        overflow: hidden;
                        min-height: 145px;
                        border-radius: 22px;

                        background:
                            linear-gradient(
                                120deg,
                                #062A4B 0%,
                                #063A6B 58%,
                                #0F6DAB 100%
                            );

                        box-shadow:
                            0 16px 38px
                            rgba(6,42,75,.16);

                        margin-bottom: 14px;
                    }

                    .students-admission-header-glow {
                        position: absolute;
                        width: 260px;
                        height: 260px;
                        border-radius: 50%;
                        right: -80px;
                        top: -145px;

                        background:
                            rgba(246,201,68,.15);

                        pointer-events: none;
                    }

                    .students-admission-header:after {
                        content: "";
                        position: absolute;
                        width: 160px;
                        height: 160px;
                        border-radius: 50%;
                        left: 42%;
                        bottom: -130px;

                        background:
                            rgba(255,255,255,.045);

                        pointer-events: none;
                    }

                    .students-admission-header-content {
                        position: relative;
                        z-index: 2;
                        min-height: 145px;
                        padding: 25px 28px;

                        display: flex;
                        align-items: center;
                        justify-content: space-between;

                        gap: 25px;
                        box-sizing: border-box;
                    }

                    .students-admission-title-group {
                        display: flex;
                        align-items: center;
                        gap: 17px;
                        min-width: 0;
                    }

                    .students-admission-icon {
                        width: 58px;
                        height: 58px;
                        min-width: 58px;
                        border-radius: 16px;

                        display: flex;
                        align-items: center;
                        justify-content: center;

                        background:
                            rgba(255,255,255,.12);

                        border:
                            1px solid
                            rgba(255,255,255,.18);

                        color: #F6C944;
                        font-size: 26px;

                        box-shadow:
                            inset 0 1px 0
                            rgba(255,255,255,.08);
                    }

                    .students-admission-eyebrow {
                        display: block;
                        color: #F6C944;
                        font-size: 10px;
                        line-height: 1;
                        font-weight: 800;
                        letter-spacing: 1.4px;
                        text-transform: uppercase;
                        margin-bottom: 7px;
                    }

                    .students-admission-title {
                        color: #fff !important;
                        margin: 0 !important;
                        font-size: 28px !important;
                        line-height: 1.08 !important;
                        font-weight: 800 !important;
                        letter-spacing: -.6px;
                    }

                    .students-admission-subtitle {
                        display: block;
                        margin-top: 7px;
                        color:
                            rgba(255,255,255,.72);
                        font-size: 12.5px;
                        line-height: 1.5;
                    }

                    .students-admission-year {
                        min-width: 175px;
                        padding: 13px 17px;
                        border-radius: 13px;

                        background:
                            rgba(0,0,0,.15);

                        border:
                            1px solid
                            rgba(255,255,255,.13);

                        backdrop-filter:
                            blur(8px);
                    }

                    .students-admission-year span {
                        display: block;
                        color:
                            rgba(255,255,255,.58);
                        font-size: 9px;
                        font-weight: 700;
                        letter-spacing: 1px;
                        text-transform: uppercase;
                        margin-bottom: 5px;
                    }

                    .students-admission-year strong {
                        display: block;
                        color: #fff;
                        font-size: 15px;
                    }

                    .students-admission-toolbar {
                        min-height: 72px;
                        padding: 12px 15px;
                        margin-bottom: 14px;

                        border-radius: 16px;

                        background:
                            var(--admission-white);

                        border:
                            1px solid
                            var(--admission-border);

                        box-shadow:
                            0 6px 22px
                            rgba(6,42,75,.055);

                        display: flex;
                        align-items: center;
                        justify-content: space-between;

                        gap: 14px;
                    }

                    .students-admission-toolbar-left {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        min-width: 0;
                        flex: 1;
                    }

                    .students-admission-search {
                        height: 43px;
                        width: min(520px, 100%);

                        display: flex;
                        align-items: center;
                        gap: 9px;

                        padding: 0 13px;

                        border-radius: 11px;

                        background:
                            var(--admission-page);

                        border:
                            1px solid
                            var(--admission-border);

                        color:
                            var(--admission-muted);
                    }

                    .students-admission-search .ant-input {
                        color:
                            var(--admission-text);

                        background: transparent;
                        font-size: 13px;
                    }

                    .students-admission-search .ant-input::placeholder {
                        color:
                            var(--admission-muted);
                    }

                    .search-result-tag {
                        margin: 0;
                        border: 0;

                        background: #FFF7E6;
                        color: #C0750D;

                        border-radius: 8px;
                    }

                    .students-admission-toolbar-actions {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex-shrink: 0;
                    }

                    .refresh-admission-btn {
                        width: 42px;
                        height: 42px;

                        border-radius: 10px;

                        color:
                            var(--admission-navy);

                        border-color:
                            var(--admission-border);

                        background:
                            var(--admission-white);
                    }

                    .students-admission-table-section {
                        overflow: hidden;
                        border-radius: 18px;

                        background:
                            var(--admission-white);

                        border:
                            1px solid
                            var(--admission-border);

                        box-shadow:
                            0 8px 28px
                            rgba(6,42,75,.065);
                    }

                    .students-admission-table-heading {
                        min-height: 76px;
                        padding: 15px 19px;

                        display: flex;
                        align-items: center;
                        justify-content: space-between;

                        gap: 15px;

                        border-bottom:
                            1px solid
                            var(--admission-border);
                    }

                    .table-heading-eyebrow {
                        display: block;
                        color:
                            var(--admission-orange);

                        font-size: 9px;
                        line-height: 1.2;
                        font-weight: 800;
                        letter-spacing: 1px;
                        text-transform: uppercase;

                        margin-bottom: 4px;
                    }

                    .table-heading-title {
                        color:
                            var(--admission-text);

                        font-size: 19px;
                        font-weight: 800;
                        line-height: 1.2;
                    }

                    .table-record-count {
                        height: 34px;
                        padding: 0 11px;

                        display: flex;
                        align-items: center;
                        gap: 7px;

                        border-radius: 9px;

                        background:
                            var(--admission-page);

                        color:
                            var(--admission-muted);

                        font-size: 11px;
                        font-weight: 700;
                    }

                    .table-record-count svg {
                        color:
                            var(--admission-orange);
                    }

                    .students-admission-table {
                        background: transparent;
                    }

                    .students-admission-table .ant-table {
                        background: transparent;
                        color:
                            var(--admission-text);
                    }

                    .students-admission-table
                        .ant-table-container {
                        border: 0 !important;
                    }

                    .students-admission-table
                        .ant-table-thead
                        > tr
                        > th {
                        background:
                            #F7F9FB !important;

                        color:
                            #596979 !important;

                        border-bottom:
                            1px solid
                            var(--admission-border) !important;

                        font-size: 10px;
                        font-weight: 800;
                        letter-spacing: .6px;
                        white-space: nowrap;
                    }

                    .students-admission-dark
                        .students-admission-table
                        .ant-table-thead
                        > tr
                        > th {
                        background:
                            #18222C !important;

                        color:
                            #B5C0CA !important;
                    }

                    .students-admission-table
                        .ant-table-tbody
                        > tr
                        > td {
                        background:
                            var(--admission-white);

                        color:
                            var(--admission-text);

                        border-bottom:
                            1px solid
                            var(--admission-border);

                        font-size: 12.5px;
                    }

                    .students-admission-table
                        .ant-table-tbody
                        > tr:hover
                        > td {
                        background:
                            rgba(246,201,68,.045)
                            !important;
                    }

                    .student-name-cell {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        min-width: 190px;
                    }

                    .student-avatar {
                        width: 35px;
                        height: 35px;
                        min-width: 35px;

                        border-radius: 10px;

                        display: flex;
                        align-items: center;
                        justify-content: center;

                        color: #063A6B;
                        background: #EAF2F8;

                        border:
                            1px solid
                            #D8E6F0;
                    }

                    .student-name-content {
                        min-width: 0;
                    }

                    .student-name-content strong {
                        display: block;

                        color:
                            var(--admission-text);

                        font-size: 12.5px;
                        line-height: 1.3;

                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .student-name-content span {
                        display: block;
                        margin-top: 2px;

                        color:
                            var(--admission-muted);

                        font-size: 9.5px;
                    }

                    .table-secondary-text {
                        color:
                            var(--admission-muted);

                        font-size: 12px;
                    }

                    .grade-class-tag {
                        margin: 0;
                        border: 0;
                        border-radius: 7px;

                        background: #EDF5FA;
                        color: #0F6DAB;

                        font-size: 10.5px;
                        font-weight: 700;
                    }

                    .grade-class-tag .anticon {
                        margin-right: 4px;
                    }

                    .status-active {
                        margin: 0;
                        border: 0;

                        background: #EDF8E8;
                        color: #3F8F1C;

                        border-radius: 7px;

                        font-weight: 700;
                        font-size: 10px;
                    }

                    .status-inactive {
                        margin: 0;
                        border: 0;

                        background: #FFF0F0;
                        color: #C53030;

                        border-radius: 7px;

                        font-weight: 700;
                        font-size: 10px;
                    }

                    .student-action-space {
                        white-space: nowrap;
                    }

                    .student-action-space
                        .ant-btn {
                        height: 31px;
                        border-radius: 7px;

                        font-size: 10.5px;
                        font-weight: 700;

                        padding: 0 9px;
                    }

                    .table-action-edit {
                        color: #063A6B;
                        border-color: #D8E6F0;
                        background: #F5F9FC;
                    }

                    .table-action-edit:hover {
                        color:
                            #0F6DAB !important;

                        border-color:
                            #0F6DAB !important;
                    }

                    .table-action-delete {
                        padding: 0 8px !important;
                    }

                    .secure-btn {
                        background:
                            #C0750D !important;

                        border-color:
                            #C0750D !important;

                        box-shadow:
                            0 4px 10px
                            rgba(192,117,13,.14);
                    }

                    .students-admission-loader {
                        min-height: 310px;

                        display: flex;
                        flex-direction: column;

                        align-items: center;
                        justify-content: center;

                        gap: 12px;

                        color:
                            var(--admission-muted);

                        font-size: 12px;
                    }

                    .students-admission-loader-ring {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }

                    .students-admission-loader-ring span {
                        width: 8px;
                        height: 8px;

                        border-radius: 50%;

                        background: #C0750D;

                        animation:
                            admissionLoader
                            1.1s
                            infinite
                            ease-in-out;
                    }

                    .students-admission-loader-ring
                        span:nth-child(2) {
                        animation-delay: .15s;
                        background: #EFA81C;
                    }

                    .students-admission-loader-ring
                        span:nth-child(3) {
                        animation-delay: .3s;
                        background: #063A6B;
                    }

                    @keyframes admissionLoader {
                        0%,
                        80%,
                        100% {
                            transform: scale(.65);
                            opacity: .45;
                        }

                        40% {
                            transform: scale(1);
                            opacity: 1;
                        }
                    }

                    .admission-download-section {
                        margin-top: 14px;
                    }

                    @media (max-width: 900px) {
                        .students-admission-page {
                            padding: 12px;
                        }

                        .students-admission-header-content {
                            align-items: flex-start;
                            flex-direction: column;
                        }

                        .students-admission-year {
                            width: 100%;
                            box-sizing: border-box;
                        }

                        .students-admission-toolbar {
                            align-items: stretch;
                            flex-direction: column;
                        }

                        .students-admission-toolbar-left {
                            width: 100%;
                        }

                        .students-admission-search {
                            width: 100%;
                        }

                        .students-admission-toolbar-actions {
                            justify-content: flex-end;
                        }
                    }

                    @media (max-width: 600px) {
                        .students-admission-page {
                            padding: 8px;
                        }

                        .students-admission-header {
                            border-radius: 16px;
                        }

                        .students-admission-header-content {
                            min-height: auto;
                            padding: 19px 16px;
                        }

                        .students-admission-title-group {
                            align-items: flex-start;
                            gap: 12px;
                        }

                        .students-admission-icon {
                            width: 45px;
                            height: 45px;
                            min-width: 45px;

                            border-radius: 12px;
                            font-size: 20px;
                        }

                        .students-admission-title {
                            font-size: 22px !important;
                        }

                        .students-admission-subtitle {
                            font-size: 11px;
                        }

                        .students-admission-toolbar {
                            padding: 10px;
                            border-radius: 13px;
                        }

                        .students-admission-toolbar-left {
                            flex-direction: column;
                            align-items: stretch;
                        }

                        .students-admission-toolbar-actions {
                            width: 100%;
                        }

                        .students-admission-toolbar-actions > * {
                            flex: 1;
                        }

                        .students-admission-table-section {
                            border-radius: 13px;
                        }

                        .students-admission-table-heading {
                            padding: 13px;
                        }

                        .table-heading-title {
                            font-size: 16px;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default StudentsAdmission;